import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import DetailDialog from "../components/DetailDialog";
import EntityLink from "../components/EntityLink";
import ModuleContextChip from "../components/ModuleContextChip";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { rowOpenProps } from "../hooks/useRowOpen";
import {
    CONTRACT_STATUS_LABEL,
    CONTRACT_TYPE_LABEL,
    SALARY_TYPE_LABEL,
    daysUntil,
    formatCurrency,
    formatDate,
    statusBadgeClass,
} from "../lib/domain";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import useDelayedFlag from "../hooks/useDelayedFlag";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import { Checkbox } from "../components/ui/checkbox";
import useBulkSelection from "../hooks/useBulkSelection";
import BulkActionsBar from "../components/BulkActionsBar";
import { useHasPermission } from "../contexts/PermissionsContext";

const ALL = "__all__";

export default function Contracts() {
    const { modules, byId: modulesById } = useModules();
    const scopedId = useScopedModuleId();
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const [filters, setFilters] = useState({
        module_id: scopedId || ALL,
        contract_type: ALL,
        status: ALL,
        expiring: false,
    });
    const [contracts, setContracts] = useState([]);
    const [employees, setEmployees] = useState({});
    const [loading, setLoading] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);
    const [detail, setDetail] = useState(null);
    // Iter56 → Iter56c — bulk selection, gated identically to individual row
    // actions so dept mgrs with module-scoped grants get the toolbar too.
    const canBulkWrite = useHasPermission("contracts.write", scopedId || null);
    const canBulkDelete = useHasPermission("contracts.delete", scopedId || null);
    const bulk = useBulkSelection(contracts, (c) => c.id);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        if (filters.contract_type !== ALL) params.contract_type = filters.contract_type;
        if (filters.status !== ALL) params.status = filters.status;
        if (showDeleted) params.include_deleted = true;
        try {
            const { data } = filters.expiring
                ? await api.get("/contracts/expiring", { params: { days: 30 } })
                : await api.get("/contracts", { params });
            setContracts(data);
            // load employees for these contracts
            const empIds = [...new Set(data.map((c) => c.employee_id))];
            if (empIds.length) {
                const empResp = await api.get("/employees", {
                    params: { page_size: 200 },
                });
                const map = {};
                empResp.data.items.forEach((e) => {
                    map[e.id] = e;
                });
                setEmployees(map);
            } else {
                setEmployees({});
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        load();
    }, [filters.module_id, filters.contract_type, filters.status, filters.expiring, showDeleted]);

    const counts = useMemo(() => {
        const out = { total: contracts.length, active: 0, expiring: 0, expired: 0 };
        contracts.forEach((c) => {
            if (c.status === "active") out.active += 1;
            if (c.status === "expired") out.expired += 1;
            const d = daysUntil(c.end_date);
            if (c.end_date && d !== null && d >= 0 && d <= 30 && c.status !== "terminated")
                out.expiring += 1;
        });
        return out;
    }, [contracts]);

    return (
        <AppShell title="Contracts">
            <section className="space-y-5" data-testid="contracts-page">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <SummaryCard label="Total contracts" value={counts.total} />
                    <SummaryCard label="Active" value={counts.active} />
                    <SummaryCard label="Expiring ≤30d" value={counts.expiring} accent="amber" />
                    <SummaryCard label="Expired" value={counts.expired} accent="rose" />
                </div>

                <div className="asl-card p-4 flex flex-wrap items-end gap-3">
                    {/* Iter60.p13 — hide Module dropdown when inside a workspace. */}
                    {scopedId ? (
                        <ModuleContextChip moduleId={scopedId} testid="contracts-module-chip" />
                    ) : (
                        <FilterSelect
                            label="Module"
                            value={filters.module_id}
                            onValueChange={(v) => setFilters((f) => ({ ...f, module_id: v }))}
                            items={[
                                { value: ALL, label: "All modules" },
                                ...modules.map((m) => ({ value: m.id, label: m.name })),
                            ]}
                            testid="contracts-filter-module"
                        />
                    )}
                    <FilterSelect
                        label="Type"
                        value={filters.contract_type}
                        onValueChange={(v) => setFilters((f) => ({ ...f, contract_type: v }))}
                        items={[
                            { value: ALL, label: "All types" },
                            ...Object.entries(CONTRACT_TYPE_LABEL).map(([v, l]) => ({
                                value: v,
                                label: l,
                            })),
                        ]}
                        testid="contracts-filter-type"
                    />
                    <FilterSelect
                        label="Status"
                        value={filters.status}
                        onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        items={[
                            { value: ALL, label: "All statuses" },
                            ...Object.entries(CONTRACT_STATUS_LABEL).map(([v, l]) => ({
                                value: v,
                                label: l,
                            })),
                        ]}
                        testid="contracts-filter-status"
                    />
                    <label
                        className="flex items-center gap-2 ml-auto text-xs cursor-pointer"
                        data-testid="contracts-expiring-toggle"
                    >
                        <input
                            type="checkbox"
                            checked={filters.expiring}
                            onChange={(e) =>
                                setFilters((f) => ({ ...f, expiring: e.target.checked }))
                            }
                        />
                        <span className="text-[color:var(--asl-muted)]">Show only expiring (≤30d)</span>
                    </label>
                    <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="contracts-show-deleted" />
                </div>

                <div className="asl-card overflow-hidden">
                    {(() => {
                        const showSkeleton = loading;
                        const filtered = filters.module_id !== ALL || filters.contract_type !== ALL || filters.status !== ALL || filters.expiring;
                        const clearFilters = () => setFilters({ module_id: scopedId || ALL, contract_type: ALL, status: ALL, expiring: false });
                        if (showSkeleton) {
                            return <TableSkeleton rows={6} cols={10} testid="contracts-loading" />;
                        }
                        if (contracts.length === 0) {
                            return (
                                <EmptyState
                                    icon={FileText}
                                    title={filtered ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No contracts yet"}
                                    description={filtered ? "Adjust filters or clear them to see all contracts." : "Open an employee profile and create a contract to get started."}
                                    filtered={filtered}
                                    onClear={clearFilters}
                                    testid="contracts-empty"
                                />
                            );
                        }
                        return null;
                    })()}
                    {!loading && contracts.length > 0 && (canBulkWrite || canBulkDelete) && (
                        <BulkActionsBar
                            resource="contracts"
                            selectedIds={bulk.selectedIds}
                            onClear={bulk.clear}
                            onRefresh={load}
                            entityLabel="contract"
                            canApprove={canBulkWrite}
                            approveLabel="Activate"
                            approveVerb="activated"
                        />
                    )}
                    {!loading && contracts.length > 0 && (
                        <table className="asl-table asl-table--zebra" data-testid="contracts-table">
                            <thead
                                className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{
                                    background: "var(--asl-navy-50)",
                                    borderBottom: "1px solid var(--asl-border)",
                                }}
                            >
                                <tr>
                                    {(canBulkWrite || canBulkDelete) && (
                                        <th className="px-3 py-3 w-8">
                                            <Checkbox
                                                checked={bulk.allVisibleSelected}
                                                onCheckedChange={bulk.toggleAll}
                                                data-testid="bulk-select-all"
                                            />
                                        </th>
                                    )}
                                    <th className="text-left px-4 py-3">Employee</th>
                                    <th className="text-left px-4 py-3">Module</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Salary</th>
                                    <th className="text-left px-4 py-3">Basic</th>
                                    <th className="text-left px-4 py-3">Benefits</th>
                                    <th className="text-left px-4 py-3">Total</th>
                                    <th className="text-left px-4 py-3">Start</th>
                                    <th className="text-left px-4 py-3">End</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                    <th className="text-right px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {contracts.map((c) => {
                                    const emp = employees[c.employee_id];
                                    const d = daysUntil(c.end_date);
                                    const expiringSoon =
                                        c.end_date && d !== null && d >= 0 && d <= 30 && c.status !== "terminated";
                                    const expired =
                                        c.end_date && d !== null && d < 0 && c.status !== "terminated";
                                    return (
                                        <tr
                                            key={c.id}
                                            {...rowOpenProps(navigate, emp ? `/m/${pathModuleCode}/staff/${emp.id}` : null, { testid: `contracts-row-${c.id}`, className: "border-t" })}
                                            style={{ borderColor: "var(--asl-border)" }}
                                        >
                                            {(canBulkWrite || canBulkDelete) && (
                                                <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={bulk.isSelected(c.id)}
                                                        onCheckedChange={() => bulk.toggle(c.id)}
                                                        data-testid={`bulk-select-${c.id}`}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-4 py-3">
                                                {emp ? (
                                                    <Link
                                                        to={`/m/${pathModuleCode}/staff/${emp.id}`}
                                                        className="asl-navy-text font-medium hover:underline"
                                                    >
                                                        {emp.full_name}
                                                    </Link>
                                                ) : (
                                                    <span className="text-[color:var(--asl-muted)]">{c.employee_id.slice(0, 8)}</span>
                                                )}
                                                {emp?.module_employee_id && (
                                                    <div className="text-[11px] mono asl-crimson-text">
                                                        {emp.module_employee_id}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs">{modulesById[c.module_id]?.name || "—"}</td>
                                            <td className="px-4 py-3 text-xs">
                                                <button
                                                    type="button"
                                                    className="asl-navy-text hover:asl-crimson-text hover:underline underline-offset-2 transition-colors cursor-pointer"
                                                    onClick={(e) => { e.stopPropagation(); setDetail(c); }}
                                                    data-testid={`contract-type-link-${c.id}`}
                                                >
                                                    {CONTRACT_TYPE_LABEL[c.contract_type] || (c.contract_type || "—").replace(/_/g, " ")}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{SALARY_TYPE_LABEL[c.salary_type]}</td>
                                            <td className="px-4 py-3 mono text-xs">{formatCurrency(c.basic_salary)}</td>
                                            <td className="px-4 py-3 mono text-xs">{formatCurrency(c.benefits_component)}</td>
                                            <td className="px-4 py-3 mono text-xs font-semibold asl-navy-text">
                                                {formatCurrency(c.total_compensation)}
                                            </td>
                                            <td className="px-4 py-3 mono text-xs">{formatDate(c.start_date)}</td>
                                            <td className="px-4 py-3 mono text-xs">
                                                {formatDate(c.end_date)}
                                                {expiringSoon && (
                                                    <span
                                                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700"
                                                        data-testid={`contract-expiring-${c.id}`}
                                                    >
                                                        in {d}d
                                                    </span>
                                                )}
                                                {expired && (
                                                    <span
                                                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700"
                                                        data-testid={`contract-expired-${c.id}`}
                                                    >
                                                        expired
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(c.status)}`}>
                                                    {CONTRACT_STATUS_LABEL[c.status]}
                                                </span>
                                                {c.deleted_at && <DeletedPill deletedAt={c.deleted_at} />}
                                            </td>
                                            <td className="px-4 py-3 text-right" data-no-row-click>
                                                <RowActions
                                                    entity="contracts"
                                                    endpoint="/contracts"
                                                    id={c.id}
                                                    label={emp?.module_employee_id || emp?.full_name || c.id.slice(0, 8)}
                                                    deleted={!!c.deleted_at}
                                                    moduleId={c.module_id}
                                                    onChange={load}
                                                    testid={`contracts-actions-${c.id}`}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>
            <DetailDialog
                row={detail}
                onClose={() => setDetail(null)}
                title="Contract Details"
                testid="contract-detail"
                moduleCodeFallback={pathModuleCode}
            />
        </AppShell>
    );
}

function SummaryCard({ label, value, accent }) {
    const colors = {
        amber: "bg-amber-50 text-amber-800",
        rose: "bg-rose-50 text-rose-800",
    }[accent];
    return (
        <div className="asl-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div
                className={`mt-1 text-3xl font-extrabold ${
                    colors ? "" : "asl-navy-text"
                }`}
            >
                <span className={colors ? `inline-block px-2 rounded ${colors}` : ""}>{value}</span>
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onValueChange, items, testid }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <Select value={value} onValueChange={onValueChange}>
                <SelectTrigger className="w-52" data-testid={testid}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {items.map((i) => (
                        <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                            {i.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
